import React, { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import {
  useGetBankHolidaysQuery,
  useCreateBankHolidayMutation,
  useDeleteBankHolidayMutation,
} from '../../../store/slices/apiSlice';
import { BankHolidayForm } from '../components/BankHolidayForm';
import { BankHolidayTable } from '../components/BankHolidayTable';

export const BankHolidaysTab: React.FC = () => {
  const toast = useToast();
  const [deletingIds, setDeletingIds] = useState<Record<string, boolean>>({});

  // Fetch bank holidays
  const {
    data: bankHolidaysData,
    isLoading: isLoadingHolidays,
    refetch: refetchHolidays,
  } = useGetBankHolidaysQuery();

  const bankHolidays = bankHolidaysData ?? [];

  // Mutations
  const [createBankHoliday, { isLoading: isCreating }] = useCreateBankHolidayMutation();
  const [deleteBankHoliday, { isLoading: isDeleting }] = useDeleteBankHolidayMutation();

  const handleCreate = async (data: { title: string; date: string }) => {
    try {
      await createBankHoliday(data).unwrap();
      toast.success(
        'Bank Holiday Added',
        `${data.title} has been added as a bank holiday.`
      );
      refetchHolidays();
    } catch (error: any) {
      const errorMessage = error?.data?.detail || error?.data?.error || 'Failed to add bank holiday';
      
      // Check for duplicate date error
      if (errorMessage.includes('unique') || errorMessage.includes('already exists')) {
        toast.error('Duplicate Date', 'A bank holiday already exists for this date.');
      } else {
        toast.error('Failed to Add', errorMessage);
      }
    }
  };

  const handleDelete = async (id: string) => {
    // Find the holiday name for the confirmation message
    const holiday = bankHolidays?.find((h) => h.id === id);
    const holidayName = holiday?.title || 'this bank holiday';
    
    if (!window.confirm(`Are you sure you want to delete "${holidayName}"?`)) {
      return;
    }

    setDeletingIds((prev) => ({ ...prev, [id]: true }));
    try {
      await deleteBankHoliday(id).unwrap();
      toast.success(
        'Bank Holiday Deleted',
        `"${holidayName}" has been removed.`
      );
      refetchHolidays();
    } catch (error: any) {
      const errorMessage = error?.data?.detail || error?.data?.error || 'Failed to delete bank holiday';
      toast.error('Deletion Failed', errorMessage);
    } finally {
      setDeletingIds((prev) => ({ ...prev, [id]: false }));
    }
  };

  // Calculate stats
  const totalHolidays = bankHolidays?.length || 0;
  const currentYear = new Date().getFullYear();
  const thisYearHolidays = bankHolidays?.filter(
    (h) => new Date(h.date).getFullYear() === currentYear
  ).length || 0;
  const upcomingHolidays = bankHolidays?.filter(
    (h) => new Date(h.date) >= new Date(new Date().setHours(0, 0, 0, 0))
  ).length || 0;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <p className="text-sm text-blue-700 font-medium">Total Bank Holidays</p>
          <p className="text-2xl font-bold text-blue-900">{totalHolidays}</p>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <p className="text-sm text-green-700 font-medium">This Year ({currentYear})</p>
          <p className="text-2xl font-bold text-green-900">{thisYearHolidays}</p>
        </div>
        <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
          <p className="text-sm text-purple-700 font-medium">Upcoming</p>
          <p className="text-2xl font-bold text-purple-900">{upcomingHolidays}</p>
        </div>
      </div>

      {/* Add Form */}
      <BankHolidayForm
        onSubmit={handleCreate}
        isSubmitting={isCreating}
      />

      {/* Bank Holidays List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Bank Holidays</h3>
          <p className="text-sm text-gray-500 mt-1">
            These dates are excluded when calculating working days for holiday requests.
          </p>
        </div>
        <div className="p-6">
          <BankHolidayTable
            bankHolidays={bankHolidays || []}
            isLoading={isLoadingHolidays}
            onDelete={handleDelete}
            isDeleting={deletingIds}
          />
        </div>
      </div>
    </div>
  );
};

export default BankHolidaysTab;
